import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Plus, Calendar, X } from "lucide-react";

const TYPES = [
  ["pto", "Paid time off (PTO)"],
  ["sick", "Sick leave"],
  ["unpaid", "Unpaid leave"],
];

export default function EmployeeLeave() {
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState({});
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const [leaves, me] = await Promise.all([
      api.get("/leave-requests"),
      api.get("/auth/me"),
    ]);
    setRows(leaves.data);
    setBalance(me.data.leave_balance || {});
  };
  useEffect(() => { load(); }, []);

  const remaining = (type) => {
    if (type === "pto") return (balance.pto_days || 0) - (balance.used_pto || 0);
    if (type === "sick") return (balance.sick_days || 0) - (balance.used_sick || 0);
    return "—";
  };

  return (
    <div data-testid="employee-leave">
      <p className="overline">Time off</p>
      <div className="flex items-center justify-between mb-6 mt-2">
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white">My leave</h1>
        <button onClick={() => setShowForm(true)} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="new-leave-btn">
          <Plus className="w-4 h-4" /> Request time off
        </button>
      </div>

      {/* Balance cards */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <BalanceCard label="PTO remaining" total={balance.pto_days || 0} used={balance.used_pto || 0} accent="emerald" />
        <BalanceCard label="Sick remaining" total={balance.sick_days || 0} used={balance.used_sick || 0} accent="amber" />
        <BalanceCard label="Pending requests" total={rows.filter((l) => l.status === "pending").length} used={0} accent="navy" hideBar />
      </div>

      <div className="card-pb overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700 font-medium text-[#0A2540] dark:text-white">
          Request history ({rows.length})
        </div>
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-[#94A3B8]">No time-off requests yet.</p>
        ) : (
          <ul>
            {rows.map((l) => (
              <li key={l.leave_id} className="px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 last:border-0 flex items-start justify-between" data-testid={`leave-${l.leave_id}`}>
                <div>
                  <div className="text-sm font-medium text-[#0A2540] dark:text-white capitalize inline-flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#C9A356]" />
                    {l.leave_type} · {l.days} day{l.days > 1 ? "s" : ""}
                    <span className="text-[10px] font-mono text-[#94A3B8]">{l.leave_number}</span>
                  </div>
                  <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">{l.start_date} → {l.end_date}</div>
                  {l.reason && <div className="text-[11px] text-[#94A3B8] mt-0.5 italic">"{l.reason}"</div>}
                  {l.decision_note && <div className="text-[11px] text-[#0A2540] dark:text-slate-300 mt-0.5">Manager: {l.decision_note}</div>}
                </div>
                <StatusPill status={l.status} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {showForm && <LeaveForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} remaining={remaining} />}
    </div>
  );
}

function BalanceCard({ label, total, used, accent, hideBar }) {
  const remaining = total - used;
  const pct = total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;
  const map = { emerald: "text-emerald-600 bg-emerald-500", amber: "text-amber-600 bg-amber-500", navy: "text-[#0A2540] bg-[#0A2540] dark:text-white" };
  const [txt, bg] = (map[accent] || map.navy).split(" ");
  return (
    <div className="card-pb p-5">
      <div className="overline">{label}</div>
      <div className={`text-3xl font-bold tabular-nums font-heading mt-1 ${txt}`}>{hideBar ? total : remaining}</div>
      {!hideBar && (
        <>
          <div className="text-[11px] text-[#94A3B8] mt-1">of {total} available · {used} used</div>
          <div className="mt-3 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className={bg} style={{ width: `${pct}%`, height: "100%" }} />
          </div>
        </>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    denied: "bg-red-100 text-red-700",
    cancelled: "bg-slate-200 text-slate-600",
  };
  return <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${map[status] || "bg-slate-100 text-slate-700"}`}>{status}</span>;
}

function LeaveForm({ onClose, onCreated, remaining }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ leave_type: "pto", start_date: today, end_date: today, reason: "" });
  const [saving, setSaving] = useState(false);

  const days = (() => {
    const s = new Date(form.start_date);
    const e = new Date(form.end_date);
    return Math.max(1, Math.round((e - s) / (24 * 3600 * 1000)) + 1);
  })();

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/leave-requests", { ...form, days });
      toast.success("Leave request submitted");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setSaving(false); }
  };

  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="leave-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">Request time off</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1">Type</label>
            <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="leave-type" className={cls}>
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l} — {remaining(v)} left</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1">Start</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" className={cls} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1">End</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" className={cls} />
            </div>
          </div>
          <div className="text-xs text-[#475569] dark:text-slate-400">Total: <span className="font-bold text-[#0A2540] dark:text-white">{days}</span> day{days > 1 ? "s" : ""}</div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1">Reason (optional)</label>
            <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" className={cls} placeholder="Family trip, medical, personal…" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-accent text-sm" data-testid="submit-leave">
            {saving ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}
